import type { QuestionContextVideo } from "@ui/organisms/QuestionBox/QuestionContextVideos";
import type { AcceptedEffortUpgrade } from "@ui/organisms/QuestionBox/useUpgradableEffort";
import { createEffect, createMemo, createSignal, Show } from "solid-js";
import { latestAcceptedEffortUpgrade } from "~/modules/shared/chat/chat-panel/acceptedEffortUpgrade";
import type { ChatPanelController } from "~/modules/shared/chat/chat-panel/ChatPanel";
import type { ChatPanelUiState } from "~/modules/shared/chat/chat-panel/chatPanelState";
import { ChatCardToolbarActionsProvider } from "~/modules/shared/chat/chat-panel/components/ChatCardToolbar";
import { conversationContextVideos } from "~/modules/shared/chat/chat-panel/conversationContextVideos";
import { AskPageConversation } from "./AskPageConversation";

export type ThreadQuestion = {
	id: string;
	question: string;
	summary: string;
	answer: string;
};

type Props = {
	handle: string;
	/**
	 * The thread's questions, oldest first. They share one conversation,
	 * streamed from the latest when the thread is rendered.
	 */
	questions: ThreadQuestion[];
	/**
	 * Questions the viewer is linked to. The viewer may rate and save any
	 * answer in a thread that has one of them.
	 */
	ownedQuestionIds?: string[];
	/** The creator's own social account public ids; their avatars are kept out of the thinking ticker. */
	creatorSocialAccountIds?: readonly string[];
	/**
	 * Called whenever the conversation has nothing left in progress: once its
	 * answers are complete, and again after each follow-up's.
	 */
	onComplete?: () => void;
	/**
	 * Called with the videos the thread's answers have mentioned so far, in
	 * thread order, whenever they change: what a follow-up can mention.
	 */
	onContextVideos?: (videos: QuestionContextVideo[]) => void;
	/** Called with the latest effort upgrade accepted in the thread, whenever it changes. */
	onAcceptedEffortUpgrade?: (
		upgrade: AcceptedEffortUpgrade | undefined,
	) => void;
	/** Called with the thread's controller as its conversation is created, before it loads. */
	onController?: (controller: ChatPanelController) => void;
};

/**
 * Renders a public AskPage thread, or a shared snapshot of one: the root
 * question and every follow-up are interactions of a single conversation,
 * rendered in full by the shared chat transcript ({@link AskPageConversation})
 * from GET .../questions/{id}/stream. A follow-up added to the thread is
 * streamed into the same conversation with its controller's
 * `refreshConversation()`, which asks for only what came after the last
 * interaction on screen.
 *
 * `questions.chat_log` is never written by anything in this codebase, so the
 * answers always stream: ones given in a previous visit replay quickly, one
 * still in progress streams live.
 */
export const AskPageThread = (props: Props) => {
	const [conversation, setConversation] = createSignal<ChatPanelUiState>();
	// Held so the question never changes under the conversation as follow-ups
	// are added to `props.questions`.
	const streamedQuestionId = props.questions.at(-1)?.id;

	const contextVideos = createMemo(() =>
		conversationContextVideos({
			interactions: conversation()?.interactions ?? [],
		}),
	);
	createEffect(() => props.onContextVideos?.(contextVideos()));
	const acceptedEffortUpgrade = createMemo(() =>
		latestAcceptedEffortUpgrade(conversation()?.interactions ?? []),
	);
	createEffect(() => props.onAcceptedEffortUpgrade?.(acceptedEffortUpgrade()));

	// The server's own explanation when there is one: the rate-limit and
	// concurrency responses say something actionable ("wait for one to
	// finish"), which a generic failure message would throw away.
	const [errored, setErrored] = createSignal<string | null>(null);

	const owned = () => (props.ownedQuestionIds?.length ?? 0) > 0;

	return (
		<div class="ask-page__thread">
			<Show when={streamedQuestionId}>
				{(questionId) => (
					<ChatCardToolbarActionsProvider save={owned()} rate={owned()}>
						<AskPageConversation
							handle={props.handle}
							questionId={questionId()}
							loadRatings={owned()}
							creatorSocialAccountIds={props.creatorSocialAccountIds}
							onAnswered={() => props.onComplete?.()}
							onState={setConversation}
							onController={(controller) => props.onController?.(controller)}
							onSettled={(error) => {
								if (error === undefined) {
									setErrored(null);
								} else {
									setErrored(
										error.trim() !== ""
											? error
											: "Something went wrong generating this answer — reload the page to try again.",
									);
									props.onComplete?.();
								}
							}}
						/>
					</ChatCardToolbarActionsProvider>
				)}
			</Show>
			<Show when={errored()}>
				{(message) => (
					<div class="ask-page__thread-item">
						<p class="ask-page__thread-pending">{message()}</p>
					</div>
				)}
			</Show>
		</div>
	);
};
